import { useState } from 'react'
import { toast } from 'sonner'
import { useStartTranslation } from '@/features/ai/useStartTranslation'
import { useTranslationQuote, type TranslationQuoteView } from '@/features/ai/useTranslationQuote'
import { useReserveReturning } from '@/features/ai/useReserveReturning'
import type { StartFailure } from '@/features/ai/lib/parseStartError'
import { failureText } from '@/features/ai/lib/failureText'
import { useIsCourseFrozen } from '@/features/moderation/useCourseModeration'
import { refusedByReview } from '@/features/editor/lib/reviewGuard'
import { useHasUnsavedBlockDraft } from '../../studio/aiSidebar/useUnsavedBlockGate'
import { translateGates } from './translateGates'

export interface TranslationLauncher {
  quote: TranslationQuoteView
  translateImages: boolean
  setTranslateImages: (v: boolean) => void
  /** Входят ли картинки в ПОКАЗАННУЮ цену — единственный источник и для
   *  панели, и для подтверждений: показанная цена = отправленная. */
  showsImages: boolean
  /** Что уедет в `translate_images` — прежнее правило вкладки «Языки». */
  willTranslateImages: boolean
  canSoft: boolean
  canHard: boolean
  quoteFailed: boolean
  hasUnsaved: boolean
  /** Курс на проверке: перевод пишет контент заявки — обе кнопки погашены. */
  isFrozen: boolean
  isStarting: boolean
  failure: StartFailure | null
  reserveReturning: boolean
  /** Каким режимом стартовали в последний раз — им же и повторяем после
   *  отказа: у разрушительного другая цена и своё подтверждение. */
  lastOverwrite: boolean
  run: (overwrite: boolean) => void
  /** Старт из диалога подтверждения. Меню к этому моменту закрыто, и баннер
   *  отказа в панели никто не увидит — отказ дублируется тостом с тем же
   *  заголовком, что у `FailureBanner`. */
  runConfirmed: (overwrite: boolean) => void
}

/**
 * Запуск перевода из меню языка шапки. Мягкий режим (`overwrite: false`) и
 * разрушительный («перевести заново») — два разных старта: у второго цена
 * ДРУГАЯ (`overwriteTotalCost`), и подтверждение у него обязательное — его
 * рисует `TranslationDialogs`, здесь только логика.
 *
 * `startBlocked` — свой прогон уже идёт (реестр джоб) или летит запрос языкового
 * действия: смета и курс ещё не сошлись. Смета об этом ещё не
 * знает: тег `Quote` протухает только на терминальном статусе, поэтому сразу
 * после `201` `active_job` в ней пуст — без этого признака второй клик уходил
 * бы в `409`.
 */
export const useTranslationLauncher = (
  courseId: number,
  startBlocked: boolean,
): TranslationLauncher => {
  const quote = useTranslationQuote(courseId, false)
  const { start, isStarting, failure } = useStartTranslation()
  // Платная точка старта: перевод пишет в те же блоки, что правка и
  // отрисовка. Предикат ОБЩИЙ, своей копии условия здесь нет.
  const hasUnsaved = useHasUnsavedBlockDraft()
  const reserveReturning = useReserveReturning(courseId)
  const isFrozen = useIsCourseFrozen(courseId)
  // Спека §05 и макет: «Картинки — по выбору», по умолчанию сняты — дольше и
  // дороже. Раньше в настройках тумблер стоял включённым; решение прототипа.
  const [translateImages, setTranslateImages] = useState(false)
  const [lastOverwrite, setLastOverwrite] = useState(false)
  // Выбирать нечего, когда картинок в плане нет.
  const hasImages = Boolean(quote.imagesCount)
  // Подтверждённый ноль и НЕИЗВЕСТНОСТЬ — разные вещи, и `Boolean(undefined)`
  // их путает. Чтение сметы защитное (`Partial` в `useTranslationQuote`), а
  // запуск гасится только по `pendingUnits` — значит ответ с `pending_units`,
  // но без `images_count`, оставляет кнопку живой.
  const imagesKnown = quote.imagesCount !== undefined
  // Что УЕДЕТ на сервер. Подтверждённый ноль шлёт серверный дефолт `true`
  // (автор ничего не отключал, а цепочка при нуле картинок не заводится в
  // любом случае), НЕИЗВЕСТНОСТЬ — то же, что показано. Иначе получается
  // худший исход задачи: на кнопке цена без картинок, а бэк заводит цепочку и
  // списывает с картинками.
  const willTranslateImages = hasImages ? translateImages : imagesKnown
  // Перезапрос сметы (меню перезапрашивает её на каждом открытии) тоже гасит
  // старт: пока он летит, на экране прежние `has_outdated` и цена, и быстрый
  // клик ушёл бы без подтверждения актуализации (§6.2) и по старой цене.
  // `quote.isLoading` — это `isFetching` запроса, а не только первая загрузка.
  const busy =
    quote.activeJob !== null ||
    startBlocked ||
    isStarting ||
    hasUnsaved ||
    quote.isLoading ||
    isFrozen
  // Числа плана приходят ТОЛЬКО из сметы: без неё неизвестно ни сколько единиц
  // уедет в прогон, ни во сколько это обойдётся. Сами гейты — чистой функцией.
  const { quoteFailed, canSoft, canHard } = translateGates(quote, busy)
  const run = (overwrite: boolean): Promise<StartFailure | null> => {
    // Страховка: `runConfirmed` зовёт диалог подтверждения, открытый ещё до
    // того, как опрос принёс `pending`, — кнопки к этому моменту уже гаснут.
    if (refusedByReview(isFrozen)) return Promise.resolve(null)
    setLastOverwrite(overwrite)
    // Ожидаемое число картинок — для общей полосы (§6.4), только если они
    // правда уедут в прогон.
    const imagesTotal = willTranslateImages ? quote.imagesCount : undefined
    return start(courseId, overwrite, willTranslateImages, imagesTotal)
  }
  return {
    quote,
    translateImages,
    setTranslateImages,
    showsImages: hasImages && translateImages,
    willTranslateImages,
    canSoft,
    canHard,
    quoteFailed,
    hasUnsaved,
    isFrozen,
    isStarting,
    failure,
    reserveReturning,
    lastOverwrite,
    run: (overwrite) => void run(overwrite),
    runConfirmed: (overwrite) =>
      void run(overwrite).then((failure) => {
        if (failure) toast.error(failureText(failure, { reserveReturning }).title)
      }),
  }
}
